/**
 * API Client
 * Simple fetch wrapper for making API requests
 */

import { API_BASE_URL } from './config';
import { throwIfNotOk } from '../utils/apiErrors';

export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

/**
 * Type-safe helper to extract data from API response
 * Handles both wrapped { data: T } and unwrapped T responses
 */
export function unwrapData<T>(response: ApiResponse<T>): T {
  return response.data;
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;

    // Don't set Content-Type for FormData (browser will set it with boundary)
    const isFormData = options?.body instanceof FormData;
    const headers: HeadersInit = isFormData
      ? { ...options?.headers }
      : {
          'Content-Type': 'application/json',
          ...options?.headers,
        };

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    // persona-audit L1 (MAJOR): this used to read only `error.message` from the
    // parsed body, but the backend's error middleware (errorHandler.ts's
    // classifyError) serialises the actual message under `error.error`, not
    // `error.message` — so every specific backend message (e.g. "Invalid
    // status transition from PLANNED to VERIFIED...") was silently discarded
    // in favour of the generic `HTTP Error: 400` fallback. `throwIfNotOk`
    // (apiErrors.ts) already has the correct field-preference logic (and
    // 5xx-body-suppression) shared with every other mutation error path in
    // the app — reuse it here instead of hand-rolling a second, buggier
    // extractor. It also attaches `.status` to the thrown `ApiError`, so
    // downstream helpers (isConflictError, isForbiddenError, ...) work for
    // every caller of this client, not just the ones using fetch() directly.
    await throwIfNotOk(response, `HTTP Error: ${response.status}`);

    // Handle 204 No Content responses (e.g., DELETE)
    if (response.status === 204) {
      return { data: null as T, success: true };
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    // Don't stringify FormData
    const body = data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined);

    return this.request<T>(endpoint, {
      method: 'POST',
      body,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient(API_BASE_URL);
export const apiClient = api; // Alias for compatibility
export default api;
